import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { WEEKDAYS, fmtDate, fmtLong } from "./calendarUtils";

export const GeneratePanel = ({ year, course, meta, onGenerated }) => {
  const sch = meta?.schedule;
  const [form, setForm] = useState({ weekday: String(sch?.weekday ?? 0), start_time: sch?.start_time || "", start_date: sch?.start_date || "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const recital = meta?.recital_date;
  const body = { course, weekday: Number(form.weekday), start_time: form.start_time, start_date: form.start_date };

  const doPreview = async () => {
    if (!form.start_time || !form.start_date) return toast.error("Compila orario di inizio e data di partenza");
    setBusy(true);
    try { const { data } = await api.post("/staff/lessons/preview", body, { params: { year } }); setPreview(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/staff/lessons/generate", body, { params: { year } });
      toast.success(`Calendario generato: ${data.created} lezioni`); setPreview(null); onGenerated();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const kept = preview ? preview.kept.moved + preview.kept.cancelled + preview.kept.recovery : 0;

  return (
    <div className="rounded-lg border border-border p-4 space-y-3" data-testid="generate-panel">
      <p className="text-sm font-medium text-foreground">Genera lezioni settimanali</p>
      {!recital ? (
        <p data-testid="generate-no-recital" className="text-sm rounded-lg bg-amber-500/15 text-amber-900 p-3 flex gap-2"><AlertTriangle size={16} className="shrink-0 mt-0.5" /> Data del saggio mancante: le lezioni vengono generate fino al saggio. Impostala nella scheda <strong>Anno &amp; Corsi → Docenti e saggi</strong>.</p>
      ) : <p className="text-xs text-muted-foreground">Fino al saggio del {fmtDate(recital)} (compreso). Le chiusure vengono saltate.</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label className="text-xs">Giorno della settimana</Label>
          <Select value={form.weekday} onValueChange={(v) => setForm({ ...form, weekday: v })}>
            <SelectTrigger data-testid="generate-weekday" className="bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>{WEEKDAYS.map((w, i) => <SelectItem key={w} value={String(i)}>{w}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Ora di inizio</Label><Input data-testid="generate-time" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1 bg-white" /></div>
        <div><Label className="text-xs">Data di partenza</Label><Input data-testid="generate-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 bg-white" />
          {form.start_date && <p data-testid="generate-start-weekday" className="mt-1 text-sm text-primary first-letter:uppercase">{fmtLong(form.start_date)}</p>}
        </div>
      </div>
      <Button data-testid="generate-preview" disabled={!recital || busy} onClick={doPreview} className="bg-primary text-primary-foreground hover:bg-primary/90"><Wand2 size={15} className="mr-1.5" /> Anteprima</Button>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="generate-preview-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Anteprima calendario</DialogTitle></DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p data-testid="preview-count"><strong>{preview.count}</strong> lezioni regolari · {WEEKDAYS[Number(form.weekday)]} {form.start_time}</p>
              <p data-testid="preview-range">Prima: <strong>{fmtDate(preview.first)}</strong> · Ultima: <strong>{fmtDate(preview.last)}</strong></p>
              <div data-testid="preview-skipped">
                <p className="text-muted-foreground">Saltate per chiusura: {preview.skipped.length || "nessuna"}</p>
                {preview.skipped.length > 0 && <ul className="list-disc pl-5">{preview.skipped.map((s) => <li key={s.date}>{fmtDate(s.date)}{s.description ? ` — ${s.description}` : ""}</li>)}</ul>}
              </div>
              {preview.skipped_manual.length > 0 && <p className="text-muted-foreground" data-testid="preview-skipped-manual">Non ricreate (già spostate o annullate a mano): {preview.skipped_manual.map(fmtDate).join(", ")}</p>}
              {(preview.replace_regular > 0 || kept > 0) && (
                <div data-testid="preview-replace-warning" className="rounded-lg bg-amber-500/15 text-amber-900 p-3 space-y-1">
                  <p className="font-medium flex items-center gap-1.5"><AlertTriangle size={15} /> Rigenerazione</p>
                  <p>Verranno sostituite <strong>{preview.replace_regular}</strong> lezioni regolari esistenti con {preview.count} nuove.</p>
                  <p>Modifiche manuali mantenute: <strong>{kept}</strong> ({preview.kept.moved} spostate, {preview.kept.cancelled} annullate, {preview.kept.recovery} recuperi).</p>
                  {preview.moved_list.length > 0 && <ul className="list-disc pl-5" data-testid="preview-moved-list">{preview.moved_list.map((m) => <li key={`${m.date}${m.start_time}`}><strong>Spostata</strong>: {fmtDate(m.original_date)} → {fmtDate(m.date)} {m.start_time}</li>)}</ul>}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" data-testid="generate-cancel" onClick={() => setPreview(null)}>Annulla</Button>
            <Button data-testid="generate-confirm" disabled={busy || !preview?.count} onClick={doGenerate} className="bg-primary text-primary-foreground hover:bg-primary/90">{preview && (preview.replace_regular > 0 || kept > 0) ? "Conferma e sostituisci" : "Salva calendario"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
